import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fadeUp } from "@/lib/motion-presets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Menu = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { add, setOpen } = useCart();

  useEffect(() => {
    axios.get(`${API}/menu`).then((res) => {
      setItems(res.data.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  const order = ["Main Plates", "Signature Plates", "Sides"];

  return (
    <section id="menu" className="relative py-24 sm:py-32" data-testid="menu-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="eyebrow mb-4">The Menu</div>
            <h2 className="font-display text-5xl sm:text-7xl tracking-tighter leading-none">
              Plates of the <span className="italic" style={{ color: "var(--awd-ochre)" }}>People</span>
            </h2>
          </div>
          <p className="max-w-md text-base" style={{ color: "var(--awd-text-2)" }}>
            Each plate is hand-prepared the morning of pickup. We cook in small batches — when they're gone, they're gone.
          </p>
        </div>

        {loading && <div className="text-center py-20" style={{ color: "var(--awd-text-3)" }}>Loading menu…</div>}

        {order.map((cat) => grouped[cat] && (
          <div key={cat} className="mb-20" data-testid={`menu-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="flex items-baseline gap-4 mb-8 border-b pb-4" style={{ borderColor: "var(--awd-border)" }}>
              <h3 className="font-display text-3xl sm:text-4xl tracking-tight">{cat}</h3>
              <span className="label-mini" style={{ color: "var(--awd-ochre)" }}>
                {cat === "Main Plates" ? "Full Meal · $20" : cat === "Signature Plates" ? "House Favorites · $16" : "Add-ons · $6"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {grouped[cat].map((it, i) => (
                <MenuCard key={it.id} item={it} index={i} onAdd={() => { add(it, 1); setOpen(true); }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const MenuCard = ({ item, index, onAdd }) => (
  <motion.div
    {...fadeUp}
    transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
    className="menu-card group flex flex-col"
    data-testid={`menu-item-${item.id}`}
  >
    <div className="img-box h-[280px] md:h-[320px]">
      <img src={item.image} alt={item.name} className="absolute inset-0 w-full h-full object-cover img-warm" />
      <div className="absolute top-3 right-3 px-3 py-1.5 font-display text-lg" style={{ background: "var(--awd-bg)", color: "var(--awd-ochre)" }}>
        ${item.price.toFixed(0)}
      </div>
    </div>
    <div className="p-6 sm:p-7 flex flex-col flex-grow">
      <h4 className="font-display text-2xl mb-2">{item.name}</h4>
      <p className="text-sm leading-relaxed flex-grow" style={{ color: "var(--awd-text-2)" }}>{item.description}</p>
      <button
        onClick={onAdd}
        className="mt-6 inline-flex items-center justify-between w-full px-5 py-3.5 border text-sm uppercase tracking-widest font-semibold transition-colors duration-300 hover:bg-[#EAA636] hover:text-[#0f0d0c] hover:border-[#EAA636]"
        style={{ borderColor: "var(--awd-border-2)", color: "var(--awd-text)" }}
        data-testid={`add-to-cart-${item.id}`}
      >
        Add to Order <Plus size={16} />
      </button>
    </div>
  </motion.div>
);

export default Menu;
